import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  TextField,
  Typography
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function StaffingProfileCompletePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invite, setInvite] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    mobile: '',
    address: '',
    qualification: '',
    course: '',
    specialization: '',
    experience_years: '',
    emergency_contact: '',
    emergency_name: ''
  });
  const [resume, setResume] = useState(null);
  const [educationProof, setEducationProof] = useState(null);
  const [otherDocs, setOtherDocs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      if (!API_URL) {
        setError('App is not configured (missing REACT_APP_API_URL).');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/public/staffing-invite/${token}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.status === 410) {
          setError(data.error || 'This link has expired.');
          setLoading(false);
          return;
        }
        if (!res.ok || !data.success) {
          setError(data.error || 'Invalid or expired link.');
          setLoading(false);
          return;
        }
        setInvite(data);
        setForm((f) => ({
          ...f,
          full_name: data.candidate_name || '',
          email: data.email || '',
          mobile: data.mobile || ''
        }));
      } catch {
        if (!cancelled) setError('Could not load this page. Check your connection.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (token) load();
    else setError('Missing invite token.');
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setDoneMessage('');
    setError('');
    if (!resume) {
      setError('Please upload your resume.');
      return;
    }
    if (!API_URL) {
      setError('App is not configured (missing REACT_APP_API_URL).');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
      fd.append('resume', resume);
      if (educationProof) fd.append('education_proof', educationProof);
      otherDocs.forEach((file) => fd.append('other_documents', file));

      const res = await fetch(`${API_URL}/api/public/staffing-invite/${token}/submit`, {
        method: 'POST',
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Submit failed');
      }
      setDoneMessage(data.message || 'Thank you. Your profile has been submitted.');
      setInvite((prev) => (prev ? { ...prev, profile_completed: true } : prev));
    } catch (err) {
      setError(err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
        <LinearProgress />
        <Typography align="center" sx={{ mt: 2 }} color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  if (error && !invite) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (invite?.profile_completed || doneMessage) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ py: 4 }}>
            <Typography variant="h5" fontWeight={800} gutterBottom align="center">
              Profile submitted
            </Typography>
            <Typography color="text.secondary" align="center">
              {doneMessage || 'Your details and documents have been received. The employer will review your profile and contact you with next steps.'}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: 4, mb: 6, px: 2 }}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Complete your profile
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {invite?.company_name} has invited you to share your details and documents.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={2}>
              <TextField
                required
                label="Full name"
                value={form.full_name}
                onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                required
                type="email"
                label="Email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Mobile"
                value={form.mobile}
                onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Address"
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              <TextField
                label="Qualification"
                value={form.qualification}
                onChange={(e) => setForm((p) => ({ ...p, qualification: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Course"
                value={form.course}
                onChange={(e) => setForm((p) => ({ ...p, course: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Specialization"
                value={form.specialization}
                onChange={(e) => setForm((p) => ({ ...p, specialization: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Experience (years)"
                value={form.experience_years}
                onChange={(e) => setForm((p) => ({ ...p, experience_years: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Emergency contact name"
                value={form.emergency_name}
                onChange={(e) => setForm((p) => ({ ...p, emergency_name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Emergency contact number"
                value={form.emergency_contact}
                onChange={(e) => setForm((p) => ({ ...p, emergency_contact: e.target.value }))}
                fullWidth
                size="small"
              />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Resume (required)
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Choose file
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setResume(e.target.files?.[0] || null)}
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {resume?.name || 'No file selected'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Education proof (optional)
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Choose file
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEducationProof(e.target.files?.[0] || null)}
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {educationProof?.name || 'No file selected'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Other documents (optional, up to 5)
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Choose files
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setOtherDocs(Array.from(e.target.files || []).slice(0, 5))}
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {otherDocs.length ? `${otherDocs.length} file(s)` : 'No files selected'}
                </Typography>
              </Box>

              <Button type="submit" variant="contained" size="large" disabled={submitting} sx={{ mt: 1 }}>
                {submitting ? 'Submitting…' : 'Submit profile'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
